import {
  ArrowRight, BrainCircuit, CalendarDays, Check, ChevronRight, Files,
  MessageCircleQuestion, Route, ShieldCheck, Sparkles, Target
} from "lucide-react";
import { Link } from "react-router";
import { PageMeta } from "../components/PageMeta";
import { releaseManifest } from "../data/release-info";

const routeSteps = [
  ["01", "目标", "明确这次要学会什么"],
  ["02", "结构", "看清关键概念如何相连"],
  ["03", "例子", "把概念放进真实情境"],
  ["04", "练习", "离开资料独立作答"],
  ["05", "纠错", "根据证据补最小缺口"],
  ["06", "复习", "在遗忘前主动回忆"]
] as const;

const capabilities = [
  { icon: Files, title: "资料内化", text: "PDF、课件和讲义先提取、去重、归类，再绑定到课程中的具体一步。" },
  { icon: Route, title: "路线学习", text: "先看全局，再一次只学一格；任意小课都能重学，不被“最新进度”绑住。" },
  { icon: MessageCircleQuestion, title: "AI 老师", text: "讲直觉、换例子、追问细节，也能用一句话带你去软件里的正确入口。" },
  { icon: ShieldCheck, title: "掌握证据", text: "手动完成与系统核验分开记录，关键评分可复核、质疑、撤回。" },
  { icon: CalendarDays, title: "记忆与复习", text: "日历、日记、到期复习和多轮学习连续保存，下一次从真实位置接上。" },
  { icon: BrainCircuit, title: "模型自由", text: "开箱即用，也能连接自己的兼容 API 或 Code Client，并按任务选择模型。" }
] as const;

export function HomePage() {
  return (
    <main>
      <PageMeta title="把资料学成能力" description="StudyCoach 把庞杂课程资料拆成清晰路线，让 AI 老师陪你从下一步学到真正掌握。" />

      <section className="hero hero--compact shell">
        <div className="hero-copy">
          <div className="eyebrow"><span className="live-dot" /> 稳定版 {releaseManifest.version}</div>
          <h1>别想整门课。<br /><em>只学下一步。</em></h1>
          <p>StudyCoach 把教材和课件变成可走的课程路线；AI 老师陪你理解、作答和复习，学到哪里、是否经过系统核验，都清楚留下。</p>
          <div className="hero-actions">
            <Link className="button button--pink" to="/download">下载 StudyCoach <ArrowRight size={18} /></Link>
            <Link className="text-link" to="/roadmap">查看产品路线 <ChevronRight size={16} /></Link>
          </div>
          <div className="hero-trust">
            <span><Check size={14} /> 本地优先</span>
            <span><Check size={14} /> 来源可追溯</span>
            <span><Check size={14} /> 进度独立保存</span>
          </div>
        </div>

        <div className="product-stage" aria-label="StudyCoach 当前课程学习路线示意">
          <span className="product-stage__disclosure">界面示意</span>
          <div className="product-window">
            <div className="window-bar">
              <div className="mini-brand"><img src="/brand/studycoach.svg" alt="" /><span><strong>StudyCoach</strong><small>一门新课程</small></span></div>
              <span className="window-status"><span /> 当前在学</span>
            </div>
            <div className="route-title">
              <span>本单元学习路线</span>
              <strong>6 步，从目标走到独立运用</strong>
              <small>已完成 4 / 6</small>
            </div>
            <div className="route-grid">
              {routeSteps.map(([number, type, title], index) => (
                <div key={number} className={`route-card${index < 4 ? " is-complete" : index === 4 ? " is-current" : ""}`}>
                  <span className="route-number">{index < 4 ? <Check size={12} /> : number}</span>
                  <small>{type}</small>
                  <strong>{title}</strong>
                </div>
              ))}
            </div>
            <div className="teacher-card">
              <span className="teacher-label"><Sparkles size={14} /> AI 老师 · 当前一步</span>
              <p>先别背结论。我们只做一件事：用自己的话说清这个概念解决什么问题。</p>
              <div className="teacher-check"><Target size={17} /><span><strong>检查理解</strong>举一个能区分它与相近概念的例子。</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="capability-section">
        <div className="shell capability-layout">
          <div className="capability-heading">
            <span className="eyebrow">从资料到能力</span>
            <h2>资料、学习、作答和复习，<br />沿一条路接起来。</h2>
          </div>
          <div className="capability-list">
            {capabilities.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <Icon size={18} aria-hidden="true" />
                <strong>{title}</strong>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="privacy" className="privacy-compact shell">
        <div>
          <span className="eyebrow">Local-first</span>
          <h2>学习记录首先属于你的设备。</h2>
        </div>
        <p>课程、进度、日记和证据默认保存在本地；密钥由系统钥匙串保管。使用 AI 时，只发送当前任务需要的资料片段与对话。跨厂商备用需要你明确授权后才会启用。<strong>本地优先不等于零联网，边界会如实说明。</strong></p>
        <Link className="text-link" to="/roadmap">查看数据与产品治理 <ChevronRight size={16} /></Link>
      </section>
    </main>
  );
}
